import { useState } from "react";
import {
  PageContainer,
  PageContent,
  PageHeader,
} from "../components/layout/PageHeader";
import { Alert } from "../components/ui/Alert";
import { Breadcrumb } from "../components/ui/Breadcrumb";
import { Button } from "../components/ui/Button";
import { Card, CardContent, CardHeader } from "../components/ui/Card";
import { Input } from "../components/ui/Input";
import { ConfirmModal } from "../components/ui/Modal";
import { Spinner } from "../components/ui/Spinner";
import {
  type User,
  useAdminUsers,
  useDeleteUser,
  useUpdateUserRole,
} from "../hooks/api/useUsers";
import { isInternalUser } from "../lib/domain";
import { UserFutureReservationsModal } from "../pages/admin-users/UserFutureReservationsModal";
import { useAuthStore } from "../stores/authStore";
import { useToastStore } from "../stores/toastStore";

export function AdminUsersPage() {
  const { user: currentUser } = useAuthStore();
  const [search, setSearch] = useState("");
  const { data, isLoading, error } = useAdminUsers(search || undefined);
  const updateRole = useUpdateUserRole();
  const deleteUser = useDeleteUser();

  const addToast = useToastStore((s) => s.addToast);
  const [roleChangeTarget, setRoleChangeTarget] = useState<User | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<User | null>(null);
  const [cancelTarget, setCancelTarget] = useState<User | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const handleRoleChange = async () => {
    if (!roleChangeTarget) {
      return;
    }
    setActionError(null);

    const newRole = roleChangeTarget.role === "admin" ? "user" : "admin";
    try {
      await updateRole.mutateAsync({
        email: roleChangeTarget.email,
        role: newRole,
      });
      addToast(`${roleChangeTarget.name}のロールを変更しました`);
      setRoleChangeTarget(null);
    } catch (err) {
      setActionError(
        err instanceof Error ? err.message : "ロール変更に失敗しました"
      );
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) {
      return;
    }
    setActionError(null);

    try {
      await deleteUser.mutateAsync(deleteTarget.email);
      addToast(`${deleteTarget.name}を削除しました`);
      setDeleteTarget(null);
    } catch (err) {
      setActionError(
        err instanceof Error ? err.message : "ユーザー削除に失敗しました"
      );
    }
  };

  const users = data?.users ?? [];

  return (
    <PageContainer>
      <PageHeader />
      <PageContent>
        <h1 className="sr-only">ユーザー管理</h1>
        <Breadcrumb
          items={[
            { label: "管理", to: "/admin/users" },
            { label: "ユーザー管理" },
          ]}
        />
        <div className="space-y-6">
          <Card>
            <CardHeader
              title={`ユーザー一覧${data ? `（${data.total}件）` : ""}`}
            />
            <CardContent>
              {/* 検索 */}
              <div className="mb-4">
                <Input
                  id="user-search"
                  type="text"
                  placeholder="名前またはメールで検索..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  aria-label="名前またはメールで検索"
                  data-testid="user-search"
                />
              </div>

              {actionError && (
                <Alert
                  variant="error"
                  className="mb-4"
                  onClose={() => setActionError(null)}
                >
                  {actionError}
                </Alert>
              )}

              {isLoading && (
                <div
                  className="flex items-center justify-center py-8"
                  role="status"
                  aria-label="読み込み中"
                >
                  <Spinner size="lg" />
                </div>
              )}

              {error && (
                <Alert variant="error">
                  ユーザー一覧の取得に失敗しました。再読み込みしてください。
                </Alert>
              )}

              {!(isLoading || error) && (
                <UserListContent
                  users={users}
                  currentUserEmail={currentUser?.email}
                  onRoleChange={setRoleChangeTarget}
                  onDelete={setDeleteTarget}
                  onShowFutureReservations={setCancelTarget}
                />
              )}
            </CardContent>
          </Card>
        </div>
      </PageContent>

      {/* ロール変更確認モーダル */}
      <ConfirmModal
        isOpen={!!roleChangeTarget}
        onClose={() => {
          setRoleChangeTarget(null);
          setActionError(null);
        }}
        onConfirm={handleRoleChange}
        title="ロール変更"
        message={
          roleChangeTarget
            ? `${roleChangeTarget.name}（${roleChangeTarget.email}）のロールを「${roleChangeTarget.role === "admin" ? "一般ユーザー" : "管理者"}」に変更しますか？`
            : ""
        }
        confirmText="変更する"
        variant="primary"
        isLoading={updateRole.isPending}
      >
        {actionError && (
          <Alert variant="error" className="mt-3">
            {actionError}
          </Alert>
        )}
      </ConfirmModal>

      {/* 削除確認モーダル */}
      <ConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => {
          setDeleteTarget(null);
          setActionError(null);
        }}
        onConfirm={handleDelete}
        title="ユーザー削除"
        message={
          deleteTarget
            ? `${deleteTarget.name}（${deleteTarget.email}）を削除しますか？過去分を含むすべての予約と連続予約の設定も削除されます。この操作は取り消せません。`
            : ""
        }
        confirmText="削除する"
        variant="danger"
        isLoading={deleteUser.isPending}
      >
        {actionError && (
          <Alert variant="error" className="mt-3">
            {actionError}
          </Alert>
        )}
      </ConfirmModal>

      {/* 全予約キャンセルモーダル */}
      <UserFutureReservationsModal
        isOpen={!!cancelTarget}
        onClose={() => setCancelTarget(null)}
        targetUser={cancelTarget}
      />
    </PageContainer>
  );
}

interface UserListContentProps {
  users: User[];
  currentUserEmail: string | undefined;
  onRoleChange: (user: User) => void;
  onDelete: (user: User) => void;
  onShowFutureReservations: (user: User) => void;
}

function UserListContent({
  users,
  currentUserEmail,
  onRoleChange,
  onDelete,
  onShowFutureReservations,
}: UserListContentProps) {
  return (
    <>
      {/* モバイル: カード表示 */}
      <div className="space-y-3 md:hidden">
        {users.length === 0 ? (
          <p className="text-sm text-gray-500">ユーザーが見つかりません</p>
        ) : (
          users.map((u) => (
            <UserCard
              key={u.email}
              user={u}
              isSelf={currentUserEmail === u.email}
              onRoleChange={onRoleChange}
              onDelete={onDelete}
              onShowFutureReservations={onShowFutureReservations}
            />
          ))
        )}
      </div>

      {/* デスクトップ: テーブル表示 */}
      <div className="hidden md:block overflow-hidden rounded-lg border border-gray-200">
        <table
          className="min-w-full divide-y divide-gray-200"
          aria-label="ユーザー一覧"
        >
          <thead className="bg-gray-50">
            <tr>
              <th
                scope="col"
                className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
              >
                名前
              </th>
              <th
                scope="col"
                className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
              >
                メール
              </th>
              <th
                scope="col"
                className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
              >
                ロール
              </th>
              <th
                scope="col"
                className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
              >
                操作
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {users.length === 0 ? (
              <tr>
                <td
                  colSpan={4}
                  className="px-4 py-8 text-center text-sm text-gray-500"
                >
                  ユーザーが見つかりません
                </td>
              </tr>
            ) : (
              users.map((u) => (
                <UserRow
                  key={u.email}
                  user={u}
                  isSelf={currentUserEmail === u.email}
                  onRoleChange={onRoleChange}
                  onDelete={onDelete}
                  onShowFutureReservations={onShowFutureReservations}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

function RoleBadge({ role }: { role: "user" | "admin" }) {
  const styles =
    role === "admin"
      ? "bg-primary-100 text-primary-700"
      : "bg-gray-100 text-gray-700";
  const label = role === "admin" ? "管理者" : "一般ユーザー";

  return (
    <span
      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${styles}`}
    >
      {label}
    </span>
  );
}

function ExternalBadge() {
  return (
    <span className="inline-flex rounded-full bg-warning-100 px-2 py-0.5 text-xs font-medium text-warning-800">
      外部
    </span>
  );
}

interface UserCardProps {
  user: User;
  isSelf: boolean;
  onRoleChange: (user: User) => void;
  onDelete: (user: User) => void;
  onShowFutureReservations: (user: User) => void;
}

function UserCard({
  user,
  isSelf,
  onRoleChange,
  onDelete,
  onShowFutureReservations,
}: UserCardProps) {
  const isExternal = !isInternalUser(user.email);
  const canChangeRole = !isExternal || user.role === "admin";

  return (
    <Card padding="sm" className="border border-gray-200 shadow-none space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-900">{user.name}</span>
          {isExternal && <ExternalBadge />}
        </div>
        <RoleBadge role={user.role} />
      </div>
      <p className="text-sm text-gray-500">{user.email}</p>
      <div className="flex gap-2">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => onShowFutureReservations(user)}
          aria-label={`${user.name}の予約全キャンセル`}
          className="flex-1"
        >
          予約全キャンセル
        </Button>
        {!isSelf && (
          <>
            {canChangeRole ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => onRoleChange(user)}
                aria-label={`${user.name}のロールを${user.role === "admin" ? "一般ユーザー" : "管理者"}に変更`}
                className="flex-1"
              >
                {user.role === "admin" ? "一般に変更" : "管理者に変更"}
              </Button>
            ) : (
              <Button
                variant="secondary"
                size="sm"
                disabled
                title="外部ユーザーのため管理者への昇格はできません"
                aria-label={`${user.name}を管理者に変更（外部ユーザーのため昇格不可）`}
                className="flex-1"
              >
                管理者に変更
              </Button>
            )}
            <Button
              variant="danger"
              size="sm"
              onClick={() => onDelete(user)}
              aria-label={`${user.name}を削除`}
              className="flex-1"
            >
              削除
            </Button>
          </>
        )}
      </div>
      {isSelf && (
        <>
          <p className="text-xs text-gray-600">自分自身のアカウント</p>
          <span className="sr-only">ロール変更・削除はできません</span>
        </>
      )}
    </Card>
  );
}

interface UserRowProps {
  user: User;
  isSelf: boolean;
  onRoleChange: (user: User) => void;
  onDelete: (user: User) => void;
  onShowFutureReservations: (user: User) => void;
}

function UserRow({
  user,
  isSelf,
  onRoleChange,
  onDelete,
  onShowFutureReservations,
}: UserRowProps) {
  const isExternal = !isInternalUser(user.email);
  const canChangeRole = !isExternal || user.role === "admin";

  return (
    <tr>
      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-900">
        <span className="inline-flex items-center gap-2">
          {user.name}
          {isExternal && <ExternalBadge />}
          {isSelf && <span className="text-xs text-gray-600">（自分）</span>}
        </span>
      </td>
      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">
        {user.email}
      </td>
      <td className="whitespace-nowrap px-4 py-3 text-sm">
        <RoleBadge role={user.role} />
      </td>
      <td className="whitespace-nowrap px-4 py-3 text-sm">
        <div className="flex gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => onShowFutureReservations(user)}
            aria-label={`${user.name}の予約全キャンセル`}
          >
            予約全キャンセル
          </Button>
          {isSelf && (
            <span className="sr-only">
              自分のアカウントのためロール変更・削除はできません
            </span>
          )}
          {!isSelf && (
            <>
              {canChangeRole ? (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => onRoleChange(user)}
                  aria-label={`${user.name}のロールを${user.role === "admin" ? "一般ユーザー" : "管理者"}に変更`}
                >
                  {user.role === "admin" ? "一般に変更" : "管理者に変更"}
                </Button>
              ) : (
                <Button
                  variant="secondary"
                  size="sm"
                  disabled
                  title="外部ユーザーのため管理者への昇格はできません"
                  aria-label={`${user.name}を管理者に変更（外部ユーザーのため昇格不可）`}
                >
                  管理者に変更
                </Button>
              )}
              <Button
                variant="danger"
                size="sm"
                onClick={() => onDelete(user)}
                aria-label={`${user.name}を削除`}
              >
                削除
              </Button>
            </>
          )}
        </div>
      </td>
    </tr>
  );
}
